import { Session } from 'next-auth';
import { apiService } from './api.service';
import { API_CONFIG } from '@/lib/config/api.config';
import { FormAnswer, User } from '@/lib/models/client.types';
import { FormData } from '@/types';

class FormService {
  async createFormAnswer(
    formData: FormData,
    session: Session
  ): Promise<string | null> {
    try {
      const response = await apiService.post<{ id: string }>(
        API_CONFIG.ENDPOINTS.FORM.CREATE,
        {
          ...formData,
          userId: session.user.id,
        },
        session
      );
      return response?.id || null;
    } catch (error) {
      console.error('Error creating form answer:', error);
      throw error;
    }
  }

  async updateFormAnswer(
    formAnswer: FormData,
    id: string,
    session: Session
  ): Promise<boolean> {
    try {
      const response = await apiService.patch<FormAnswer>(
        API_CONFIG.ENDPOINTS.FORM.UPDATE(id),
        {
          userType: formAnswer.userType,
          excludedSituation: formAnswer.excludedSituation,
          propertyOwnership: formAnswer.propertyOwnership,
          singleOwnership: formAnswer.singleOwnership,
          spouseOwnership: formAnswer.spouseOwnership,
          leaseTermType: formAnswer.leaseTermType,
          leaseDuration: formAnswer.leaseDuration,
          buildingType: formAnswer.buildingType,
          buildingAgeUnder5: formAnswer.buildingAgeUnder5,
          tenant65Over: formAnswer.tenant65Over || false,
          additionalInfo: formAnswer.additionalInfo,
        },
        session
      );
      return !!response;
    } catch (error) {
      console.error('Error updating form answer:', error);
      return false;
    }
  }

  async getFormAnswerById(
    formAnswerId: string,
    session: Session
  ): Promise<FormAnswer | null> {
    try {
      const response = await apiService.get<FormAnswer>(
        API_CONFIG.ENDPOINTS.FORM.GET_BY_ID(formAnswerId),
        session
      );

      if (!response) {
        console.warn(`No form answer found for ID: ${formAnswerId}`);
        return null;
      }

      return response;
    } catch (error) {
      console.error('Error fetching form answer:', error);

      if (error instanceof SyntaxError) {
        console.warn(
          `Invalid JSON response for form answer ID: ${formAnswerId}`
        );
        return null;
      }

      throw error;
    }
  }

  async getFormAnswer(
    session: Session
  ): Promise<FormAnswer | null> {
    try {
      const response = await apiService.get<FormAnswer>(
        API_CONFIG.ENDPOINTS.FORM.GET,
        session
      );

      if (!response) {
        console.warn(`No form answer found for user`);
        return null;
      }

      return response;
    } catch (error) {
      console.error('Error fetching form answer by user ID:', error);

      if (error instanceof SyntaxError) {
        console.warn(
          `Invalid JSON response for user`
        );
        return null;
      }

      throw error;
    }
  }

  async getProfile(userId: string, session: Session) {
    return apiService.get<User>(API_CONFIG.ENDPOINTS.USERS.BASE, session);
  }

  async updateProfile(userId: string, data: Partial<User>, session: Session) {
    return apiService.patch<User>(
      API_CONFIG.ENDPOINTS.USERS.UPDATE(userId),
      data,
      session
    );
  }
}

export const formService = new FormService();
